import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { BLD_INFO, MAX_BARRACKS_LVL, MAX_TOWER_LVL, MAX_WALL_LVL, TEAM_COLORS } from '../network/protocol';
import type { Building, Lake, ResNode, Shot } from '../network/protocol';

const BLD_SIZE: Record<string, number> = {
  townCenter: 7,
  house: 3,
  farm: 3,
  barracks: 5,
  tower: 3,
  wall: 2,
  gate: 2,
};

export function bldSize(kind: string): number {
  return BLD_SIZE[kind] ?? 3;
}

// Shared Lambert materials by color+emissive so dozens of buildings share a
// handful of GPU programs/state instead of allocating per building. Trim
// (team color) stays per-owner in BuildingRenderer.
const sharedLamCache = new Map<string, THREE.MeshLambertMaterial>();
function lamKey(color: number, emissive = 0): string {
  return `${color.toString(16)}:${emissive.toString(16)}`;
}
function sharedLam(color: number, emissive = 0): THREE.MeshLambertMaterial {
  const key = lamKey(color, emissive);
  let m = sharedLamCache.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, emissive });
    sharedLamCache.set(key, m);
  }
  return m;
}

// Gate leaf colors per tier (shared materials, never per-gate).
const DOOR_WOOD = 0x7a5230;
const DOOR_STONE = 0x596066;

// Merged-building blueprints: kind+lvl -> baked parts keyed by material.
// A Town Center drops from ~35 draws to ~6; walls/houses to 2-3. Geometries
// are baked once and shared by every instance of that kind/lvl.
type BPart = { key: string; geo: THREE.BufferGeometry };
const blueprintCache = new Map<string, BPart[]>();
const blueprintGeos = new Set<THREE.BufferGeometry>();

// Wall link directions (world axes) for corner-aware palisades.
export const WL_E = 1; // +x
export const WL_W = 2; // -x
export const WL_S = 4; // +z
export const WL_N = 8; // -z

// wallMask reports which compass directions hold a same-owner wall/gate
// neighbour (snapped segments sit exactly 2.0 apart; tolerate drift).
export function wallMask(
  x: number,
  z: number,
  walls: Array<{ x: number; z: number; o: number }>,
  owner: number,
): number {
  let mask = 0;
  for (const n of walls) {
    if (n.o !== owner || (n.x === x && n.z === z)) continue;
    const dx = n.x - x;
    const dz = n.z - z;
    if (Math.abs(dx) >= 1.0 && Math.abs(dx) <= 3.0 && Math.abs(dz) < 1.0) {
      mask |= dx > 0 ? WL_E : WL_W;
    }
    if (Math.abs(dz) >= 1.0 && Math.abs(dz) <= 3.0 && Math.abs(dx) < 1.0) {
      mask |= dz > 0 ? WL_S : WL_N;
    }
  }
  return mask;
}

// wallVariant reduces a mask to a baked blueprint: 0 = arms along X
// (isolated segments default to X), 1 = arms along Z, 2 = corner knot
// (both axes + center post). Stone segments are square and need none.
export function wallVariant(mask: number): number {
  const ax = (mask & (WL_E | WL_W)) !== 0;
  const az = (mask & (WL_S | WL_N)) !== 0;
  if (ax && az) return 2;
  if (az) return 1;
  return 0;
}

// wallVertical is the gate-orientation rule: gate frames (like the old
// palisade) are baked running east-west, so north-south chains rotate 90°.
export function wallVertical(
  x: number,
  z: number,
  walls: Array<{ x: number; z: number; o: number }>,
  owner: number,
): boolean {
  const mask = wallMask(x, z, walls, owner);
  return (mask & (WL_S | WL_N)) !== 0 && (mask & (WL_E | WL_W)) === 0;
}

// Roofline heights for the floating HP bars (generous so bars clear cones).
function bldBarHeight(kind: string, lvl = 0): number {
  switch (kind) {
    case 'townCenter':
      return 8.8;
    case 'tower':
      return 9.2;
    case 'barracks':
      return 4.8;
    case 'house':
      return 4.0;
    case 'farm':
      return 2.2;
    case 'wall':
      return lvl >= 1 ? 3.8 : 2.8;
    case 'gate':
      return lvl >= 1 ? 6.2 : 5.4;
    default:
      return 2.8;
  }
}

// Gate door leaves: baked once per side, hinged at the jamb so rotation.y
// swings them open. Left leaves extend +x from the hinge, right leaves
// mirror. Braces are merged in so each leaf stays one draw. Tier shows in
// the leaf material (planks vs iron-bound), not the shape.
function doorGeo(side: 1 | -1): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const leaf = new THREE.BoxGeometry(0.7, 2.6, 0.1);
  leaf.translate(side * 0.35, 0, 0);
  parts.push(leaf);
  for (const by of [-0.85, 0.85]) {
    const brace = new THREE.BoxGeometry(0.62, 0.16, 0.05);
    brace.translate(side * 0.35, by, 0.07);
    parts.push(brace);
  }
  const merged = mergeGeometries(parts, false) ?? leaf;
  for (const p of parts) if (p !== merged) p.dispose();
  return merged;
}

const doorGeos = {
  L: null as THREE.BufferGeometry | null,
  R: null as THREE.BufferGeometry | null,
};

function doorGeoFor(side: 1 | -1): THREE.BufferGeometry {
  const key = side === 1 ? 'L' : 'R';
  let g = doorGeos[key];
  if (!g) {
    g = doorGeo(side);
    doorGeos[key] = g;
  }
  return g;
}

// BuildingRenderer keeps one Group per building (dozens at most, so plain
// meshes are fine) with team-colored trim and a construction ghost state,
// plus one instanced HP bar floating over every building.
export class BuildingRenderer {
  private scene: THREE.Scene;
  private groups = new Map<number, THREE.Group>();
  private lvls = new Map<number, number>(); // tower upgrade tier per building
  private kinds = new Map<number, string>(); // building kind per group (catches wall→gate flips)
  private wallVars = new Map<number, number>(); // palisade corner variant per wall
  private doneState = new Map<number, boolean>(); // construction opacity state
  private gateDoors = new Map<number, { l: THREE.Mesh; r: THREE.Mesh; open: number; tier: number }>();
  private barColors: string[] = [];
  private trimMats = new Map<number, THREE.MeshLambertMaterial>();
  private bars: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private tmpColor = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.bars = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(2.4, 0.22),
      new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: false,
      }),
      64,
    );
    this.bars.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.bars.frustumCulled = false;
    this.bars.renderOrder = 5;
    for (let i = 0; i < 64; i++) this.bars.setColorAt(i, this.tmpColor.set('#4ade80'));
    scene.add(this.bars);
  }

  private trim(owner: number): THREE.MeshLambertMaterial {
    let m = this.trimMats.get(owner);
    if (!m) {
      m = new THREE.MeshLambertMaterial({
        color: new THREE.Color(TEAM_COLORS[owner] ?? '#fff'),
      });
      this.trimMats.set(owner, m);
    }
    return m;
  }

  sync(list: Building[], camera: THREE.Camera, units: Array<{ o: number; x: number; z: number }> = [], dt = 1 / 60): void {
    const seen = new Set<number>();
    let barN = 0;
    let barColorChanged = false;
    // Wall/gate neighbour lookup for palisade orientation (segments are few,
    // so a shared array scan per segment is cheaper than a map build).
    // Gates join the scan so mixed lines orient as one.
    const walls = list.filter((b) => b.k === 'wall' || b.k === 'gate');
    for (const b of list) {
      seen.add(b.id);
      // Tier-ups change the silhouette: rebuild the group once when the
      // level flips (rare — at most a few × per building). Gates ride the
      // wall tiers, so they rebuild on stone upgrades too. A kind flip
      // (wall → gate conversion) rebuilds as well, or the old palisade
      // would linger in front of the new gatehouse.
      const upg = b.k === 'tower' || b.k === 'barracks' || b.k === 'wall' || b.k === 'gate';
      const maxLvl = b.k === 'tower' ? MAX_TOWER_LVL : b.k === 'barracks' ? MAX_BARRACKS_LVL : MAX_WALL_LVL;
      const lvl = upg ? Math.min(Math.max(b.lv ?? 0, 0), maxLvl) : 0;
      // Corner variant for wooden walls (stone is square, gates orient by
      // rotation instead). Rebuilds below when a new neighbour changes it.
      const wmask = b.k === 'wall' && lvl < 1 ? wallMask(b.x, b.z, walls, b.o) : 0;
      const wvar = b.k === 'wall' && lvl < 1 ? wallVariant(wmask) : 0;
      let g = this.groups.get(b.id);
      if (g && (this.kinds.get(b.id) !== b.k || (upg && this.lvls.get(b.id) !== lvl) || this.wallVars.get(b.id) !== wvar)) {
        g.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (mesh.isMesh && !(mesh.userData?.shared ?? true)) {
            (mesh.material as THREE.Material).dispose();
          }
        });
        this.scene.remove(g);
        this.disposeGroup(g);
        this.groups.delete(b.id);
        this.lvls.delete(b.id);
        this.kinds.delete(b.id);
        this.wallVars.delete(b.id);
        this.doneState.delete(b.id);
        this.gateDoors.delete(b.id); // door leaves die with the group
        g = undefined;
      }
      if (!g) {
        g = this.buildMesh(b.k, b.o, lvl, wmask);
      g.position.set(b.x, 0, b.z);
      // Gates run along their chain: rotate north-south chains 90° so
      // neighbours merge into one line. Wooden walls bake their direction
      // (corner variants included); stone segments are square.
      if (b.k === 'gate') {
        g.rotation.y = wallVertical(b.x, b.z, walls, b.o) ? Math.PI / 2 : 0;
      } else {
        g.rotation.y = 0;
      }
        this.groups.set(b.id, g);
        this.lvls.set(b.id, lvl);
        this.kinds.set(b.id, b.k);
        this.wallVars.set(b.id, wvar);
        this.doneState.set(b.id, true); // shared mats; flipped below if needed
        this.scene.add(g);
      }
      g.position.set(b.x, 0, b.z);
      // Construction fade uses per-group cloned materials (shared blueprint
      // mats must never be tinted per building). Swap clones<->shared once
      // on the done flip, then only touch opacity while under construction.
      const done = b.p >= 1;
      const wasDone = this.doneState.get(b.id) ?? true;
      if (done !== wasDone) {
        g.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh) return;
          const cur = mesh.material as THREE.Material;
          const key = (mesh.userData?.matKey as string) ?? '';
          if (!done) {
            if ((mesh.userData?.shared ?? true)) {
              mesh.material = (cur as THREE.MeshLambertMaterial).clone();
              mesh.userData.shared = false;
            }
          } else {
            // Back to shared blueprint mats.
            if (!(mesh.userData?.shared ?? true)) {
              (cur as THREE.Material).dispose();
              mesh.material = key === 'trim'
                ? this.trim(b.o)
                : (sharedLamCache.get(key) ?? this.trim(b.o));
              mesh.userData.shared = true;
            }
            const m = mesh.material as THREE.MeshLambertMaterial;
            m.transparent = false;
            m.opacity = 1;
          }
        });
        this.doneState.set(b.id, done);
      }
      if (!done) {
        const op = 0.45 + 0.4 * b.p;
        g.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh) return;
          const m = mesh.material as THREE.MeshLambertMaterial;
          m.transparent = true;
          m.opacity = op;
        });
      }
      const s = done ? 1 : 0.35 + 0.65 * b.p;
      g.scale.set(1, s, 1);
      // Gate auto-doors: leaves swing open while any owner unit is at the
      // gate heart (the sim lets friendlies through unconditionally and
      // blocks everyone else, so proximity of the owner's units is the
      // honest open signal). eased toward the target every frame.
      if (b.k === 'gate') {
        let doors = this.gateDoors.get(b.id);
        if (!doors || doors.tier !== lvl) {
          if (doors) {
            g.remove(doors.l, doors.r);
            this.gateDoors.delete(b.id);
          }
          const color = lvl >= 1 ? DOOR_STONE : DOOR_WOOD;
          const mat = sharedLam(color);
          const key = lamKey(color);
          const l = new THREE.Mesh(doorGeoFor(1), mat);
          l.position.set(-0.7, 1.48, 0);
          l.userData.matKey = key;
          l.userData.shared = true;
          const r = new THREE.Mesh(doorGeoFor(-1), mat);
          r.position.set(0.7, 1.48, 0);
          r.userData.matKey = key;
          r.userData.shared = true;
          g.add(l, r);
          doors = { l, r, open: 0, tier: lvl };
          this.gateDoors.set(b.id, doors);
        }
        let want = 0;
        for (const u of units) {
          if (u.o !== b.o) continue;
          const dx = u.x - b.x;
          const dz = u.z - b.z;
          if (dx * dx + dz * dz < 12.25) {
            want = 1;
            break;
          }
        }
        const step = Math.max(-dt * 2.5, Math.min(dt * 2.5, want - doors.open));
        doors.open += step;
        doors.l.rotation.y = doors.open * 1.85;
        doors.r.rotation.y = -doors.open * 1.85;
      }
      // Floating bar above the roofline. Under construction it is a
      // yellow progress bar (fills with build progress); once complete it
      // becomes the HP bar — green at full, yellow/red when damaged.
      if (barN < 64) {
        const frac = done ? Math.max(b.hp / b.m, 0) : Math.max(b.p, 0);
        this.dummy.position.set(b.x, bldBarHeight(b.k, lvl), b.z);
        this.dummy.quaternion.copy(camera.quaternion);
        this.dummy.scale.set(Math.max(frac, 0.001), 1, 1);
        this.dummy.updateMatrix();
        this.bars.setMatrixAt(barN, this.dummy.matrix);
        this.dummy.quaternion.identity();
        this.dummy.scale.set(1, 1, 1);
        const color = !done
          ? '#eab308'
          : frac > 0.6 ? '#4ade80' : frac > 0.3 ? '#fbbf24' : '#ef4444';
        if (this.barColors[barN] !== color) {
          this.barColors[barN] = color;
          this.bars.setColorAt(barN, this.tmpColor.set(color));
          barColorChanged = true;
        }
        barN++;
      }
    }
    for (const [id, g] of this.groups) {
      if (!seen.has(id)) {
        // Dispose per-group construction clones; shared blueprint mats/geos
        // stay cached.
        g.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (mesh.isMesh && !(mesh.userData?.shared ?? true)) {
            (mesh.material as THREE.Material).dispose();
          }
        });
        this.scene.remove(g);
        this.disposeGroup(g);
        this.groups.delete(id);
        this.lvls.delete(id);
        this.kinds.delete(id);
        this.wallVars.delete(id);
        this.doneState.delete(id);
        this.gateDoors.delete(id);
      }
    }
    this.bars.count = barN;
    this.bars.instanceMatrix.needsUpdate = true;
    if (barColorChanged && this.bars.instanceColor) this.bars.instanceColor.needsUpdate = true;
  }

  // Merged instancing: buildMesh returns a Group of 2-7 meshes sharing
  // cached blueprint geometries (one per material bucket). First build of a
  // kind+lvl bakes the blueprint from the raw builder below; every later
  // building of that kind reuses the baked geos — ~6 draws for a TC instead
  // of ~35, zero per-building geometry garbage. Wooden walls additionally
  // key on their corner variant so L-junctions seal with no stubs or gaps.
  private buildMesh(kind: string, owner: number, lvl = 0, mask = 0): THREE.Group {
    const key = kind === 'wall' && lvl < 1 ? `${kind}:${lvl}:v${wallVariant(mask)}` : `${kind}:${lvl}`;
    let parts = blueprintCache.get(key);
    if (!parts) {
      const raw = this.buildRaw(kind, 0, lvl, mask);
      parts = this.compileBlueprint(raw, 0);
      // Free the raw temp meshes (their geometries are per-build garbage;
      // the compiled merged geos are what we cache). Never touch shared
      // trim materials.
      const shared = new Set([...this.trimMats.values(), ...sharedLamCache.values()]);
      raw.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          (mesh.geometry as THREE.BufferGeometry).dispose();
          const mat = mesh.material as THREE.Material;
          if (!shared.has(mat as THREE.MeshLambertMaterial)) mat.dispose();
        }
      });
      blueprintCache.set(key, parts);
    }
    const g = new THREE.Group();
    for (const p of parts) {
      const mat = p.key === 'trim' ? this.trim(owner) : (sharedLamCache.get(p.key) ?? this.trim(owner));
      const mesh = new THREE.Mesh(p.geo, mat);
      mesh.userData.matKey = p.key;
      mesh.userData.shared = true;
      g.add(mesh);
    }
    return g;
  }

  private buildRaw(kind: string, owner: number, lvl = 0, mask = 0): THREE.Group {
    const g = new THREE.Group();
    const trim = this.trim(owner);
    const stone = new THREE.MeshLambertMaterial({ color: 0xcfc3a5 });
    const wood = new THREE.MeshLambertMaterial({ color: 0x8a5a2b });
    const roof = new THREE.MeshLambertMaterial({ color: 0xa8442a });
    const gray = new THREE.MeshLambertMaterial({ color: 0x8d9299 });
    const add = (mesh: THREE.Mesh, x: number, y: number, z: number) => {
      mesh.position.set(x, y, z);
      g.add(mesh);
      return mesh;
    };
    switch (kind) {
      case 'townCenter': {
        const sand = new THREE.MeshLambertMaterial({ color: 0xd9c193 });
        const sandDark = new THREE.MeshLambertMaterial({ color: 0xb39b6d });
        const doorMat = new THREE.MeshLambertMaterial({ color: 0x4a2f18 });
        const gold = new THREE.MeshLambertMaterial({ color: 0xe3b341, emissive: 0x442a00 });
        const lampMat = new THREE.MeshLambertMaterial({ color: 0x554422, emissive: 0xcc8833 });
        // Stepped plinth.
        add(new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.5, 7.2), sandDark), 0, 0.25, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(6.6, 0.4, 6.6), stone), 0, 0.7, 0);
        // Main hall + team band.
        add(new THREE.Mesh(new THREE.BoxGeometry(6, 2.4, 6), sand), 0, 2.1, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(6.3, 0.45, 6.3), trim), 0, 3.4, 0);
        // Pillared portico (front, +z).
        const colGeo = new THREE.CylinderGeometry(0.22, 0.26, 2.4, 8);
        for (const cx of [-1.65, -0.55, 0.55, 1.65]) {
          add(new THREE.Mesh(colGeo, stone), cx, 2.0, 3.15);
        }
        add(new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.5, 0.7), trim), 0, 3.35, 3.15);
        // Door + frame, flanked by hanging banners.
        add(new THREE.Mesh(new THREE.BoxGeometry(1.9, 2.3, 0.1), trim), 0, 2.0, 3.02);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.5, 2.0, 0.12), doorMat), 0, 1.9, 3.08);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.2, 0.06), trim), -2.1, 2.8, 3.2);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.2, 0.06), trim), 2.1, 2.8, 3.2);
        // Entry steps.
        add(new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.3, 1.0), sandDark), 0, 0.15, 3.6);
        add(new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.3, 0.7), sandDark), 0, 0.45, 3.45);
        // Lit windows: dark recess + warm glow, front flanks and sides.
        for (const wx of [-2.3, 2.3]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.9, 0.1), doorMat), wx, 2.2, 3.02);
          add(new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.6, 0.05), lampMat), wx, 2.2, 3.06);
        }
        for (const sx of [-3.02, 3.02]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.9, 0.8), doorMat), sx, 2.2, 0);
          const lamp = add(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.6, 0.5), lampMat), sx * 1.01, 2.2, 0);
          lamp.position.x = sx + (sx > 0 ? 0.04 : -0.04);
        }
        // Upper hall + cornice.
        add(new THREE.Mesh(new THREE.BoxGeometry(4.4, 1.4, 4.4), sand), 0, 4.35, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(4.7, 0.35, 4.7), trim), 0, 5.15, 0);
        // Shikhara tiers + golden kalash.
        add(new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.7, 3.4), roof), 0, 5.67, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.7, 2.4), roof), 0, 6.37, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.7, 1.4), roof), 0, 7.07, 0);
        add(new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 6), gold), 0, 7.6, 0);
        add(new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.5, 6), gold), 0, 7.95, 0);
        // Corner chhatris: column + dome + finial on the hall roof.
        const domeGeo = new THREE.SphereGeometry(0.55, 8, 6);
        const finGeo = new THREE.ConeGeometry(0.1, 0.35, 6);
        for (const [qx, qz] of [[-2.55, -2.55], [2.55, -2.55], [-2.55, 2.55], [2.55, 2.55]] as Array<[number, number]>) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.9, 0.55), sand), qx, 4.0, qz);
          const dome = add(new THREE.Mesh(domeGeo, roof), qx, 4.6, qz);
          dome.scale.y = 0.8;
          add(new THREE.Mesh(finGeo, gold), qx, 5.1, qz);
        }
        // Flag mast with team pennant on the upper hall.
        add(new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.4, 0.12), wood), -1.8, 6.2, -1.8);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.5, 0.06), trim), -1.2, 7.2, -1.8);
        break;
      }
      case 'house': {
        const plaster = new THREE.MeshLambertMaterial({ color: 0xe6d3a8 });
        const darkWood = new THREE.MeshLambertMaterial({ color: 0x5d3a1a });
        const doorMat = new THREE.MeshLambertMaterial({ color: 0x4a2f18 });
        const thatch = new THREE.MeshLambertMaterial({ color: 0xc9a86a });
        const thatchDark = new THREE.MeshLambertMaterial({ color: 0xa9854e });
        const clay = new THREE.MeshLambertMaterial({ color: 0xb5651d });
        const lampMat = new THREE.MeshLambertMaterial({ color: 0x554422, emissive: 0xcc8833 });
        // Foundation + plaster walls in a timber frame.
        add(new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.3, 2.8), stone), 0, 0.15, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(3, 1.7, 2.6), plaster), 0, 1.15, 0);
        const hpost = new THREE.BoxGeometry(0.25, 1.8, 0.25);
        for (const [px, pz] of [[-1.35, -1.15], [1.35, -1.15], [-1.35, 1.15], [1.35, 1.15]] as Array<[number, number]>) {
          add(new THREE.Mesh(hpost, darkWood), px, 1.2, pz);
        }
        add(new THREE.Mesh(new THREE.BoxGeometry(3.1, 0.25, 0.25), darkWood), 0, 2.1, 1.2);
        add(new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.25, 2.8), trim), 0, 2.05, 0);
        // Layered thatch roof + knob.
        add(new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.3, 3.4), thatch), 0, 2.45, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.3, 2.6), thatchDark), 0, 2.85, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.3, 1.8), thatch), 0, 3.25, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.25, 1.0), thatchDark), 0, 3.55, 0);
        add(new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), clay), 0, 3.75, 0);
        // Door + frame + step (front, +z).
        add(new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.6, 0.1), trim), 0, 1.1, 1.31);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.4, 0.12), doorMat), 0, 1.0, 1.33);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.2, 0.5), stone), 0, 0.1, 1.55);
        // Front window with shutters + glow, side window.
        add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.1), doorMat), -0.95, 1.4, 1.31);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.45, 0.05), lampMat), -0.95, 1.4, 1.34);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.06), darkWood), -1.4, 1.4, 1.33);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.06), darkWood), -0.5, 1.4, 1.33);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.6, 0.6), doorMat), 1.52, 1.4, -0.3);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.4, 0.4), lampMat), 1.55, 1.4, -0.3);
        // Veranda: floor, posts, awning.
        add(new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.15, 0.8), stone), 0, 0.08, 1.75);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.3, 0.15), darkWood), -1.0, 0.8, 2.05);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.3, 0.15), darkWood), 1.0, 0.8, 2.05);
        const awn = add(new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.12, 0.9), thatchDark), 0, 1.9, 1.75);
        awn.rotation.x = 0.12;
        // Clay pots, firewood, chimney, door lantern.
        const potGeo = new THREE.SphereGeometry(0.22, 7, 6);
        const pot1 = add(new THREE.Mesh(potGeo, clay), 0.85, 0.3, 1.6);
        pot1.scale.y = 0.85;
        const pot2 = add(new THREE.Mesh(potGeo, clay), 1.2, 0.22, 1.45);
        pot2.scale.setScalar(0.7);
        const logGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.8, 6);
        for (let i = 0; i < 3; i++) {
          const log = add(new THREE.Mesh(logGeo, darkWood), -1.1, 0.42 + (i === 2 ? 0.2 : 0), 1.55 + (i === 1 ? 0.05 : 0));
          log.rotation.z = Math.PI / 2;
          if (i === 2) log.position.x = -1.1;
        }
        add(new THREE.Mesh(new THREE.BoxGeometry(0.4, 1.0, 0.4), stone), 0.9, 3.0, -0.7);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.15, 0.55), stone), 0.9, 3.55, -0.7);
        add(new THREE.Mesh(new THREE.SphereGeometry(0.11, 6, 5), lampMat), 0.7, 1.9, 1.42);
        break;
      }
      case 'barracks': {
        const darkWood = new THREE.MeshLambertMaterial({ color: 0x5d3a1a });
        const doorMat = new THREE.MeshLambertMaterial({ color: 0x3d2410 });
        const lampMat = new THREE.MeshLambertMaterial({ color: 0x554422, emissive: 0xcc8833 });
        // Stone foundation + timber walls.
        add(new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.4, 4.2), stone), 0, 0.2, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(5, 2.2, 4), wood), 0, 1.5, 0);
        // Corner posts + team wall band.
        const postGeo = new THREE.BoxGeometry(0.35, 2.4, 0.35);
        for (const [px, pz] of [[-2.3, -1.8], [2.3, -1.8], [-2.3, 1.8], [2.3, 1.8]] as Array<[number, number]>) {
          add(new THREE.Mesh(postGeo, darkWood), px, 1.6, pz);
        }
        add(new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.35, 4.2), trim), 0, 2.7, 0);
        // Gabled roof: two sloped slabs, gable infills, ridge beam.
        const slope = 0.48;
        const slabGeo = new THREE.BoxGeometry(5.6, 0.18, 2.7);
        const frontSlab = add(new THREE.Mesh(slabGeo, roof), 0, 3.35, 1.15);
        frontSlab.rotation.x = slope;
        const rearSlab = add(new THREE.Mesh(slabGeo, roof), 0, 3.35, -1.15);
        rearSlab.rotation.x = -slope;
        add(new THREE.Mesh(new THREE.BoxGeometry(4.4, 1.0, 0.15), wood), 0, 3.1, 1.9);
        add(new THREE.Mesh(new THREE.BoxGeometry(4.4, 1.0, 0.15), wood), 0, 3.1, -1.9);
        add(new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.25, 0.3), darkWood), 0, 3.95, 0);
        // Double doors + lintel + step (front, +z).
        add(new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.9, 0.12), doorMat), -0.42, 1.35, 2.02);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.9, 0.12), doorMat), 0.42, 1.35, 2.02);
        add(new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.3, 0.15), trim), 0, 2.45, 2.02);
        add(new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.25, 0.8), stone), 0, 0.12, 2.4);
        // Lit windows: front flanks + sides.
        for (const wx of [-1.7, 1.7]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.1), doorMat), wx, 1.7, 2.02);
          add(new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.45, 0.05), lampMat), wx, 1.7, 2.06);
        }
        for (const sx of [-2.52, 2.52]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.7, 0.7), doorMat), sx, 1.7, -0.5);
          add(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.45, 0.45), lampMat), sx + (sx > 0 ? 0.04 : -0.04), 1.7, -0.5);
        }
        // Weapon racks flanking the door: post + crossed spears + team shield.
        const spearGeo = new THREE.CylinderGeometry(0.05, 0.05, 1.7, 5);
        const shieldGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.08, 10);
        for (const rx of [-1.8, 1.8]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.18, 1.8, 0.18), darkWood), rx, 0.9, 2.5);
          const s1 = add(new THREE.Mesh(spearGeo, wood), rx, 1.1, 2.5);
          s1.rotation.z = 0.5;
          const s2 = add(new THREE.Mesh(spearGeo, wood), rx, 1.1, 2.5);
          s2.rotation.z = -0.5;
          const sh = add(new THREE.Mesh(shieldGeo, trim), rx, 0.9, 2.5);
          sh.rotation.x = Math.PI / 2;
        }
        // Log pile (fuel for the war effort) + chimney.
        const logGeo = new THREE.CylinderGeometry(0.22, 0.22, 1.2, 7);
        const logRow = (y: number, n: number, x0: number) => {
          for (let i = 0; i < n; i++) {
            const log = add(new THREE.Mesh(logGeo, darkWood), x0 + i * 0.45, y, -1.5);
            log.rotation.z = Math.PI / 2;
          }
        };
        logRow(0.62, 3, 1.2);
        logRow(1.0, 2, 1.4);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 1.6, 0.7), stone), 1.6, 3.6, -1.0);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.2, 0.9), stone), 1.6, 4.45, -1.0);
        // Torches flanking the door.
        for (const tx of [-1.2, 1.2]) {
          add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.2, 0.1), darkWood), tx, 0.6, 2.6);
          add(new THREE.Mesh(new THREE.SphereGeometry(0.14, 6, 5), lampMat), tx, 1.28, 2.6);
        }
        // Upgrade dressing per tier: war banners → gold-capped gatehouse.
        if (lvl >= 1) {
          // Twin team war banners on the front corners.
          for (const bx of [-2.6, 2.6]) {
            add(new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.6, 0.12), darkWood), bx, 1.3, 2.2);
            add(new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.1, 0.06), trim), bx + (bx > 0 ? 0.5 : -0.5), 2.2, 2.2);
          }
        }
        if (lvl >= 2) {
          // Gold roof crest + braziers: the Royal Akhara must shine.
          const goldMat = new THREE.MeshLambertMaterial({ color: 0xe3b341, emissive: 0x442a00 });
          add(new THREE.Mesh(new THREE.BoxGeometry(5.9, 0.18, 0.34), goldMat), 0, 4.1, 0);
          for (const bx of [-2.0, 2.0]) {
            add(new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.13, 0.3, 8), doorMat), bx, 4.3, 0);
            add(new THREE.Mesh(new THREE.SphereGeometry(0.15, 6, 5), lampMat), bx, 4.55, 0);
          }
        }
        break;
      }
      case 'tower': {
        const darkStone = new THREE.MeshLambertMaterial({ color: 0x8a8474 });
        const doorMat = new THREE.MeshLambertMaterial({ color: 0x3d2410 });
        const lampMat = new THREE.MeshLambertMaterial({ color: 0x554422, emissive: 0xcc8833 });
        // Stepped footing + battered (tapered) stone shaft.
        add(new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.5, 3.4), darkStone), 0, 0.25, 0);
        add(new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.4, 3.0), stone), 0, 0.7, 0);
        add(new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.4, 5.2, 8), stone), 0, 3.5, 0);
        // String courses + team band.
        add(new THREE.Mesh(new THREE.CylinderGeometry(1.32, 1.32, 0.25, 8), darkStone), 0, 2.2, 0);
        add(new THREE.Mesh(new THREE.CylinderGeometry(1.18, 1.18, 0.25, 8), darkStone), 0, 4.6, 0);
        add(new THREE.Mesh(new THREE.CylinderGeometry(1.12, 1.12, 0.4, 8), trim), 0, 5.6, 0);
        // Arched door + step, arrow slits, hanging war banner.
        add(new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.6, 0.12), doorMat), 0, 1.6, 1.32);
        add(new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.25, 0.6), darkStone), 0, 0.35, 1.5);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.7, 0.1), doorMat), 0.4, 3.4, 1.28);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.7, 0.18), doorMat), -1.24, 4.2, 0.2);
        add(new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.8, 0.08), trim), 0, 4.3, 1.3);
        // Hoarding deck on angled struts.
        for (const [sx, sz, rx, rz] of [[1.45, 0, 0, -0.45], [-1.45, 0, 0, 0.45], [0, 1.45, 0.45, 0], [0, -1.45, -0.45, 0]] as Array<[number, number, number, number]>) {
          const strut = add(new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.3, 0.15), wood), sx, 5.75, sz);
          strut.rotation.x = rx;
          strut.rotation.z = rz;
        }
        add(new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.9, 0.5, 8), wood), 0, 6.35, 0);
        // Railing: posts + top rail.
        const railPost = new THREE.BoxGeometry(0.12, 0.7, 0.12);
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          add(new THREE.Mesh(railPost, wood), Math.cos(a) * 1.7, 7.0, Math.sin(a) * 1.7);
        }
        const rail = add(new THREE.Mesh(new THREE.TorusGeometry(1.7, 0.06, 6, 16), wood), 0, 7.35, 0);
        rail.rotation.x = Math.PI / 2;
        // Braziers with flames on the deck.
        for (const [bx, bz] of [[1.1, 1.1], [-1.1, -1.1]] as Array<[number, number]>) {
          add(new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.14, 0.3, 8), doorMat), bx, 6.75, bz);
          add(new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), lampMat), bx, 7.0, bz);
        }
        // Spire roof + finial.
        add(new THREE.Mesh(new THREE.ConeGeometry(2.0, 1.6, 8), roof), 0, 7.8, 0);
        add(new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.4, 6), trim), 0, 8.7, 0);
        // Upgrade dressing per tier — the silhouette must read the weapon:
        // gun barrels → rocket pods → siege cannon + gold crown ring.
        const iron = new THREE.MeshLambertMaterial({ color: 0x3a3f45 });
        if (lvl >= 1) {
          // Twin gunner barrels jutting from the deck + ammo crate.
          for (const bx of [-0.6, 0.6]) {
            const barrel = add(new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 1.6, 6), iron), bx, 6.9, 1.6);
            barrel.rotation.x = Math.PI / 2 - 0.12;
          }
          add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 0.7), wood), 1.2, 6.6, -1.0);
        }
        if (lvl >= 2) {
          // Twin rocket pods with red tips flanking the deck.
          const tipMat = new THREE.MeshLambertMaterial({ color: 0xcc2222, emissive: 0x550000 });
          for (const px of [-1.3, 1.3]) {
            add(new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 1.4), iron), px, 7.1, 0.4);
            for (const pz of [0.0, 0.75]) {
              const rocket = add(new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.7, 6), wood), px, 7.1, pz);
              rocket.rotation.x = Math.PI / 2;
              add(new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.3, 6), tipMat), px, 7.1, pz + 0.5).rotation.x = Math.PI / 2;
            }
          }
        }
        if (lvl >= 3) {
          // Siege cannon: fat central barrel + powder kegs + gold crown ring.
          const cannon = add(new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.36, 2.4, 8), iron), 0, 7.3, 0.9);
          cannon.rotation.x = Math.PI / 2 - 0.18;
          const kegMat = new THREE.MeshLambertMaterial({ color: 0x2b2b2b });
          for (const [kx, kz] of [[-1.2, -1.2], [1.2, -1.2]] as Array<[number, number]>) {
            add(new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.7, 8), kegMat), kx, 6.7, kz);
          }
          const crownRing = add(new THREE.Mesh(new THREE.TorusGeometry(1.45, 0.1, 6, 16),
            new THREE.MeshLambertMaterial({ color: 0xe3b341, emissive: 0x442a00 })), 0, 6.1, 0);
          crownRing.rotation.x = Math.PI / 2;
        }
        break;
      }
      case 'gate': { // gatehouse: tall flanking bastions + tiled canopy + team plaque (leaves are dynamic)
        // Deliberately bigger than any wall (~5 units tall with a roofline
        // walls never have) so gates read instantly on the battlefield.
        if (lvl < 1) {
          // Timber gatehouse: tall jamb bastions with team bands and caps,
          // a heavy lintel with a team plaque, and a tiled gable canopy.
          const darkWood = new THREE.MeshLambertMaterial({ color: 0x5d3a1a });
          add(new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.18, 2.3), trim), 0, 0.09, 0);
          const bastionGeo = new THREE.BoxGeometry(0.6, 3.8, 0.7);
          for (const px of [-1.0, 1.0]) {
            add(new THREE.Mesh(bastionGeo, darkWood), px, 2.08, 0);
            add(new THREE.Mesh(new THREE.BoxGeometry(0.68, 0.28, 0.78), trim), px, 3.3, 0);
            add(new THREE.Mesh(new THREE.BoxGeometry(0.75, 0.22, 0.8), wood), px, 4.07, 0);
          }
          add(new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.35, 0.6), wood), 0, 3.9, 0);
          add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.3, 0.08), trim), 0, 3.9, 0.32);
          add(new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.22, 0.3), darkWood), 0, 2.92, 0);
          const slabGeo = new THREE.BoxGeometry(2.9, 0.14, 0.9);
          const slabF = add(new THREE.Mesh(slabGeo, roof), 0, 4.55, 0.42);
          slabF.rotation.x = 0.5;
          const slabR = add(new THREE.Mesh(slabGeo, roof), 0, 4.55, -0.42);
          slabR.rotation.x = -0.5;
          add(new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.16, 0.2), darkWood), 0, 4.78, 0);
        } else {
          // Stone gatehouse: ashlar bastions with capstones and team bands,
          // a heavy lintel, and the same tiled canopy — the gate identity.
          const darkStone = new THREE.MeshLambertMaterial({ color: 0x8a8474 });
          add(new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.4, 2.4), darkStone), 0, 0.2, 0);
          const bastionGeo = new THREE.BoxGeometry(0.7, 4.2, 0.8);
          for (const px of [-1.0, 1.0]) {
            add(new THREE.Mesh(bastionGeo, gray), px, 2.5, 0);
            add(new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.3, 0.88), trim), px, 3.7, 0);
            add(new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.25, 0.9), darkStone), px, 4.72, 0);
          }
          add(new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.4, 0.65), gray), 0, 4.4, 0);
          add(new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.32, 0.1), trim), 0, 4.4, 0.35);
          add(new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.25, 0.3), darkStone), 0, 3.2, 0);
          const slabGeo = new THREE.BoxGeometry(3.0, 0.14, 0.95);
          const slabF = add(new THREE.Mesh(slabGeo, roof), 0, 5.2, 0.44);
          slabF.rotation.x = 0.5;
          const slabR = add(new THREE.Mesh(slabGeo, roof), 0, 5.2, -0.44);
          slabR.rotation.x = -0.5;
          add(new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.16, 0.2), darkStone), 0, 5.43, 0);
        }
        break;
      }
      default: { // wall: lvl 0 = wooden palisade, lvl 1+ = heavy stone wall
        if (lvl < 1) {
          // Wooden palisade: pointed stake rows along each armed axis —
          // straight runs stay a clean fence while corners grow arms both
          // ways around a knot post, so L-junctions seal with no stubs or
          // gaps. Rails overlap into the neighbour cell; a slim
          // team-colored footing keeps ownership readable.
          const stakeWood = new THREE.MeshLambertMaterial({ color: 0x8a5a2b });
          const stakeDark = new THREE.MeshLambertMaterial({ color: 0x5d3a1a });
          const stakeGeo = new THREE.CylinderGeometry(0.22, 0.26, 2.0, 6);
          const tipGeo = new THREE.ConeGeometry(0.22, 0.5, 6);
          const railGeoX = new THREE.BoxGeometry(2.3, 0.18, 0.14);
          const railGeoZ = new THREE.BoxGeometry(0.14, 0.18, 2.3);
          const row = (alongZ: boolean) => {
            for (let i = -2; i <= 2; i++) {
              const c = i * 0.4;
              const mat = i % 2 === 0 ? stakeWood : stakeDark;
              const px = alongZ ? 0 : c;
              const pz = alongZ ? c : 0;
              add(new THREE.Mesh(stakeGeo, mat), px, 1.0, pz);
              add(new THREE.Mesh(tipGeo, mat), px, 2.25, pz);
            }
            // End stakes sit on the cell edge so neighbours share the post.
            for (const e of [-1.0, 1.0]) {
              const px = alongZ ? 0 : e;
              const pz = alongZ ? e : 0;
              add(new THREE.Mesh(stakeGeo, stakeDark), px, 1.0, pz);
              add(new THREE.Mesh(tipGeo, stakeDark), px, 2.25, pz);
            }
            if (alongZ) {
              add(new THREE.Mesh(railGeoZ, stakeDark), 0.24, 0.7, 0);
              add(new THREE.Mesh(railGeoZ, stakeDark), -0.24, 1.4, 0);
            } else {
              add(new THREE.Mesh(railGeoX, stakeDark), 0, 0.7, 0.24);
              add(new THREE.Mesh(railGeoX, stakeDark), 0, 1.4, -0.24);
            }
          };
          const v = wallVariant(mask);
          if (v !== 1) row(false);
          if (v !== 0) row(true);
          if (v === 2) {
            // Corner knot: square pillar tying both arms together.
            add(new THREE.Mesh(new THREE.BoxGeometry(0.42, 2.3, 0.42), stakeDark), 0, 1.15, 0);
            const knotTip = add(new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.5, 4), stakeDark), 0, 2.55, 0);
            knotTip.rotation.y = Math.PI / 4;
          }
          add(new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.18, 2.1), trim), 0, 0.09, 0);
        } else {
          // Stone wall: taller + thicker ashlar block on a wide footing,
          // team band, and crenellated parapet — visibly heavier than wood.
          const darkStone = new THREE.MeshLambertMaterial({ color: 0x8a8474 });
          add(new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.4, 2.4), darkStone), 0, 0.2, 0);
          add(new THREE.Mesh(new THREE.BoxGeometry(2.1, 2.2, 2.1), gray), 0, 1.5, 0);
          // Stone courses (horizontal seams read as masonry).
          add(new THREE.Mesh(new THREE.BoxGeometry(2.16, 0.14, 2.16), darkStone), 0, 0.9, 0);
          add(new THREE.Mesh(new THREE.BoxGeometry(2.16, 0.14, 2.16), darkStone), 0, 1.9, 0);
          // Team band + parapet slab.
          add(new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.35, 2.3), trim), 0, 2.65, 0);
          add(new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.25, 2.3), gray), 0, 2.95, 0);
          // Crenellations: merlons around the rim.
          const merlon = new THREE.BoxGeometry(0.4, 0.4, 0.4);
          for (const [mx, mz] of [[-0.85, -0.85], [0, -0.85], [0.85, -0.85], [-0.85, 0.85], [0, 0.85], [0.85, 0.85], [-0.85, 0], [0.85, 0]] as Array<[number, number]>) {
            add(new THREE.Mesh(merlon, gray), mx, 3.25, mz);
          }
        }
        break;
      }
      case 'farm': {
        // Crop field: soil bed, green rows, fence posts, scarecrow.
        const soil = new THREE.MeshLambertMaterial({ color: 0x5d4126 });
        const crop = new THREE.MeshLambertMaterial({ color: 0x4d9b3a });
        const cropDark = new THREE.MeshLambertMaterial({ color: 0x35702a });
        add(new THREE.Mesh(new THREE.BoxGeometry(3, 0.25, 2.6), soil), 0, 0.12, 0);
        for (let i = 0; i < 4; i++) {
          add(
            new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.35, 0.4), i % 2 === 0 ? crop : cropDark),
            0, 0.4, -0.9 + i * 0.6,
          );
        }
        const postGeo = new THREE.BoxGeometry(0.14, 0.7, 0.14);
        for (const [px, pz] of [[-1.4, -1.2], [1.4, -1.2], [-1.4, 1.2], [1.4, 1.2]] as Array<[number, number]>) {
          add(new THREE.Mesh(postGeo, wood), px, 0.35, pz);
        }
        // Scarecrow in the corner.
        add(new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.1, 0.12), wood), -1.0, 0.8, -0.8);
        const arms = add(new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.1, 0.1), wood), -1.0, 1.15, -0.8);
        void arms;
        add(new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), new THREE.MeshLambertMaterial({ color: 0xe8b07d })), -1.0, 1.42, -0.8);
        break;
      }
    }
    return g;
  }

  clear(): void {
    for (const [, g] of this.groups) {
      g.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh && !(mesh.userData?.shared ?? true)) {
          (mesh.material as THREE.Material).dispose();
        }
      });
      this.scene.remove(g);
      this.disposeGroup(g);
    }
    this.groups.clear();
    this.lvls.clear();
    this.kinds.clear();
    this.wallVars.clear();
    this.doneState.clear();
    this.gateDoors.clear();
    this.barColors = [];
    this.bars.count = 0;
    this.bars.instanceMatrix.needsUpdate = true;
  }

  // disposeGroup detaches a removed group. Geometries/materials are shared
  // blueprint assets (owned by caches) so nothing is disposed here — the
  // old code disposed per-building geos and leaked shared trim mats.
  private disposeGroup(_g: THREE.Group): void {
    // Intentionally empty: blueprint geos + sharedLam + trimMats are cached.
  }

  // Bake a raw multi-mesh building into merged parts grouped by material.
  // Trim (team color) is keyed as 'trim' so instances can re-skin per owner
  // while sharing the same baked shape.
  private compileBlueprint(raw: THREE.Group, owner: number): BPart[] {
    const trimInst = this.trim(owner);
    const buckets = new Map<string, THREE.BufferGeometry[]>();
    const tmp = new THREE.Matrix4();
    raw.updateMatrixWorld(true);
    raw.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mat = mesh.material as THREE.MeshLambertMaterial;
      let key: string;
      if (mat === trimInst) {
        key = 'trim';
      } else {
        const col = mat.color?.getHex?.() ?? 0xffffff;
        const em = (mat as THREE.MeshLambertMaterial).emissive?.getHex?.() ?? 0;
        key = `${col.toString(16)}:${em.toString(16)}`;
        // Register the shared material for this bucket so instances reuse it.
        sharedLam(col, em);
      }
      mesh.updateWorldMatrix(true, false);
      tmp.copy(mesh.matrixWorld);
      // Raw groups sit at origin, so local matrix == world matrix here.
      const baked = (mesh.geometry as THREE.BufferGeometry).clone().applyMatrix4(tmp);
      // Drop UV2 / extra attributes that block merging across geometry types.
      for (const name of Object.keys(baked.attributes)) {
        if (name !== 'position' && name !== 'normal' && name !== 'uv') {
          baked.deleteAttribute(name);
        }
      }
      const list = buckets.get(key) ?? [];
      list.push(baked);
      buckets.set(key, list);
    });
    const parts: BPart[] = [];
    for (const [key, geos] of buckets) {
      // mergeGeometries needs consistent attributes; normalize by keeping
      // only position/normal/uv (done above). If merge fails, keep pieces
      // as separate parts (never drop geometry).
      const merged = mergeGeometries(geos, false);
      if (merged) {
        for (const gg of geos) if (gg !== merged) gg.dispose();
        blueprintGeos.add(merged);
        parts.push({ key, geo: merged });
      } else {
        for (const gg of geos) {
          blueprintGeos.add(gg);
          parts.push({ key, geo: gg });
        }
      }
    }
    return parts;
  }

  // Placement ghost: a translucent copy of the building + a footprint quad
  // that tints green (buildable) or red (blocked). All materials are cloned
  // so the live buildings are untouched; call freeGhost to dispose. Wall
  // ghosts take the connectivity mask so corners preview sealed.
  makeGhost(kind: string, owner: number, mask = 0): THREE.Group {
    const g = this.buildMesh(kind, owner, 0, mask);
    g.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.material = (mesh.material as THREE.MeshLambertMaterial).clone();
        const m = mesh.material as THREE.MeshLambertMaterial;
        m.transparent = true;
        m.opacity = 0.55;
        m.depthWrite = false;
      }
    });
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(bldSize(kind), bldSize(kind)),
      new THREE.MeshBasicMaterial({
        color: 0x4ade80,
        transparent: true,
        opacity: 0.32,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = 0.08;
    plane.name = 'footprint';
    g.add(plane);
    // Ranged buildings (watchtower) also preview their coverage disc, so
    // the sight lines are visible before committing stone and gold.
    const range = BLD_INFO[kind]?.range ?? 0;
    if (range > 0) {
      const disc = new THREE.Mesh(
        new THREE.CircleGeometry(range, 48),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.1,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      );
      disc.rotation.x = -Math.PI / 2;
      disc.position.y = 0.04;
      disc.name = 'range';
      g.add(disc);
      const rim = new THREE.Mesh(
        new THREE.RingGeometry(range - 0.18, range, 64),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(TEAM_COLORS[owner] ?? '#ffffff'),
          transparent: true,
          opacity: 0.55,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      );
      rim.rotation.x = -Math.PI / 2;
      rim.position.y = 0.05;
      rim.name = 'range-rim';
      g.add(rim);
    }
    return g;
  }

  setGhostValid(g: THREE.Group, valid: boolean): void {
    const plane = g.getObjectByName('footprint') as THREE.Mesh | undefined;
    if (plane) {
      (plane.material as THREE.MeshBasicMaterial).color.set(valid ? '#4ade80' : '#ef4444');
    }
  }

  freeGhost(g: THREE.Group): void {
    g.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        (mesh.material as THREE.Material).dispose();
        // Blueprint geos are shared across all live buildings + ghosts —
        // only dispose the unique footprint/range helpers.
        const geo = mesh.geometry as THREE.BufferGeometry;
        if (!blueprintGeos.has(geo)) geo.dispose();
      }
    });
  }
}

// ResourceRenderer draws all resource nodes as instanced meshes.
// Wood has 3 tree types (picked by node id, stable across snapshots):
// pine cone, round mango-like crown, tall poplar. Depleted nodes shrink.
export class ResourceRenderer {
  private trunks: THREE.InstancedMesh;
  private canopies: THREE.InstancedMesh;
  private crowns: THREE.InstancedMesh;
  private poplars: THREE.InstancedMesh;
  private gold: THREE.InstancedMesh;
  private nuggets: THREE.InstancedMesh;
  private dirt: THREE.InstancedMesh;
  private bushes: THREE.InstancedMesh;
  private berries: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private tmpColor = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.trunks = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.3, 0.45, 1.6, 6),
      new THREE.MeshLambertMaterial({ color: 0x6b4423 }),
      96,
    );
    this.canopies = new THREE.InstancedMesh(
      new THREE.ConeGeometry(1.6, 3.4, 7),
      new THREE.MeshLambertMaterial({ color: 0xffffff }), // tinted per-tree
      96,
    );
    // Round mango-like crown: lumpy ball on the same trunk.
    this.crowns = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(1.6, 1),
      new THREE.MeshLambertMaterial({ color: 0xffffff }), // tinted per-tree
      96,
    );
    // Tall poplar: narrow spire for vertical accents in the forest.
    this.poplars = new THREE.InstancedMesh(
      new THREE.ConeGeometry(1.0, 4.6, 6),
      new THREE.MeshLambertMaterial({ color: 0xffffff }), // tinted per-tree
      96,
    );
    // Gold mine: central boulder + 2 satellites + 2 glowing nuggets on a
    // dirt bed. 3 rock instances + 2 nuggets + 1 disc per node.
    this.gold = new THREE.InstancedMesh(
      new THREE.DodecahedronGeometry(1.0, 0),
      new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0x664400 }),
      96,
    );
    this.nuggets = new THREE.InstancedMesh(
      new THREE.OctahedronGeometry(0.22, 0),
      new THREE.MeshBasicMaterial({ color: 0xffe066 }),
      64,
    );
    this.dirt = new THREE.InstancedMesh(
      new THREE.CircleGeometry(1.7, 12),
      new THREE.MeshLambertMaterial({ color: 0x6b5233 }),
      32,
    );
    this.bushes = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(1.1, 1),
      new THREE.MeshLambertMaterial({ color: 0xffffff }), // tinted per-bush
      48,
    );
    // Berries: bright unlit clusters studding each bush (3 per bush).
    this.berries = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.16, 6, 5),
      new THREE.MeshBasicMaterial({ color: 0xe6342e }),
      144,
    );
    for (const m of [this.trunks, this.canopies, this.crowns, this.poplars, this.gold, this.nuggets, this.dirt, this.bushes, this.berries]) {
      m.frustumCulled = false;
      scene.add(m);
    }
    // Allocate instance-color buffers up front (used for per-node tint).
    for (let i = 0; i < 96; i++) {
      this.canopies.setColorAt(i, this.tmpColor.set('#2e6b2e'));
      this.crowns.setColorAt(i, this.tmpColor.set('#3f8f3f'));
      this.poplars.setColorAt(i, this.tmpColor.set('#4a7c2e'));
      this.gold.setColorAt(i, this.tmpColor.set('#ffffff'));
      if (i < 48) this.bushes.setColorAt(i, this.tmpColor.set('#3f8f3f'));
    }
  }

  // Deterministic 0..1 jitter from a node id — stable across snapshots so
  // clustered nodes keep their individual look instead of flickering.
  private jitter(id: number): number {
    let h = (id * 2654435761) >>> 0;
    h ^= h >> 13;
    h = (h * 2246822519) >>> 0;
    return ((h % 1000) / 1000 + 1) % 1;
  }

  sync(nodes: ResNode[]): void {
    let t = 0;
    let tpn = 0;
    let tc = 0;
    let tp = 0;
    let gd = 0;
    let b = 0;
    for (const n of nodes) {
      const frac = Math.min(1, n.a / 200 + 0.25);
      const j = this.jitter(n.id);
      const spin = j * Math.PI * 2;
      if (n.k === 'wood' && t < 96) {
        const s = frac * (0.8 + j * 0.5);
        this.placeSpin(this.trunks, t, n.x, 0.8 * s, n.z, s, spin);
        const variant = n.id % 3;
        if (variant === 1 && tc < 96) {
          // Round mango-like crown: squashed ball over the trunk top.
          this.dummy.position.set(n.x, 2.3 * s, n.z);
          this.dummy.scale.set(s * 1.05, s * 0.85, s * 1.05);
          this.dummy.rotation.set(0, spin, 0);
          this.dummy.updateMatrix();
          this.crowns.setMatrixAt(tc, this.dummy.matrix);
          // Deep mango greens with sunlit variance.
          this.crowns.setColorAt(tc, this.tmpColor.setHSL(0.26 + j * 0.07, 0.5, 0.22 + j * 0.1));
          tc++;
        } else if (variant === 2 && tp < 96) {
          // Tall poplar spire rising above the trunk.
          this.dummy.position.set(n.x, 3.6 * s, n.z);
          this.dummy.scale.setScalar(s);
          this.dummy.rotation.set(0, spin, 0);
          this.dummy.updateMatrix();
          this.poplars.setMatrixAt(tp, this.dummy.matrix);
          // Fresh airs: lighter, yellower green than the pines.
          this.poplars.setColorAt(tp, this.tmpColor.setHSL(0.24 + j * 0.05, 0.5, 0.26 + j * 0.1));
          tp++;
        } else if (tpn < 96) {
          this.dummy.position.set(n.x, (1.6 + 1.7) * s, n.z);
          this.dummy.scale.setScalar(s);
          this.dummy.rotation.set(0, spin, 0);
          this.dummy.updateMatrix();
          this.canopies.setMatrixAt(tpn, this.dummy.matrix);
          // Jungle greens: hue drifts yellow-green per tree.
          this.canopies.setColorAt(tpn, this.tmpColor.setHSL(0.29 + j * 0.06, 0.45, 0.2 + j * 0.1));
          tpn++;
        }
        t++;
      } else if (n.k === 'gold' && gd < 32) {
        // Treasure pile: main boulder, two satellites, glowing nuggets on
        // top, all on a dirt bed. Tints drift amber -> bright yellow.
        const s = frac * (0.85 + j * 0.4);
        const gi = gd * 3;
        const ni = gd * 2;
        this.placeSpin(this.gold, gi, n.x, 0.55 * s, n.z, s, spin);
        this.gold.setColorAt(gi, this.tmpColor.setHSL(0.115, 0.75, 0.42 + j * 0.16));
        const a1 = j * Math.PI * 2;
        const a2 = a1 + 2.4;
        const sx1 = n.x + Math.cos(a1) * 1.15 * s;
        const sz1 = n.z + Math.sin(a1) * 1.15 * s;
        const sx2 = n.x + Math.cos(a2) * 1.0 * s;
        const sz2 = n.z + Math.sin(a2) * 1.0 * s;
        this.placeSpin(this.gold, gi + 1, sx1, 0.28 * s, sz1, s * 0.45, spin + 1);
        this.gold.setColorAt(gi + 1, this.tmpColor.setHSL(0.1, 0.7, 0.38 + j * 0.12));
        this.placeSpin(this.gold, gi + 2, sx2, 0.2 * s, sz2, s * 0.3, spin + 2);
        this.gold.setColorAt(gi + 2, this.tmpColor.setHSL(0.12, 0.8, 0.45 + j * 0.14));
        this.placeSpin(this.nuggets, ni, sx1, 0.62 * s, sz1, 0.9 + j * 0.5, spin);
        this.placeSpin(this.nuggets, ni + 1, n.x - Math.cos(a1) * 0.5 * s, 0.95 * s, n.z - Math.sin(a1) * 0.5 * s, 0.8 + j * 0.4, spin + 1);
        this.dummy.position.set(n.x, 0.02, n.z);
        this.dummy.rotation.set(-Math.PI / 2, 0, 0);
        this.dummy.scale.setScalar(Math.max(1.5 * s, 0.001));
        this.dummy.updateMatrix();
        this.dirt.setMatrixAt(gd, this.dummy.matrix);
        this.dummy.rotation.set(0, 0, 0);
        gd++;
      } else if (n.k === 'food' && b < 48) {
        const s = frac * (0.8 + j * 0.45);
        this.dummy.position.set(n.x, 0.7 * s, n.z);
        this.dummy.scale.set(s * (0.9 + j * 0.3), s * 0.75, s * (0.9 + ((j * 7) % 1) * 0.3));
        this.dummy.rotation.set(0, spin, 0);
        this.dummy.updateMatrix();
        this.bushes.setMatrixAt(b, this.dummy.matrix);
        // Berry tint: green bushes through to red-fruited ones.
        this.bushes.setColorAt(b, this.tmpColor.setHSL(0.33 - j * 0.3, 0.5, 0.28 + j * 0.06));
        // Berry cluster: 3 bright beads on the sunlit side, shrinking away
        // as the bush depletes.
        for (let k = 0; k < 3; k++) {
          const ba = spin + k * 2.1;
          this.dummy.position.set(
            n.x + Math.cos(ba) * 0.62 * s,
            (0.75 + (k % 2) * 0.3) * s,
            n.z + Math.sin(ba) * 0.62 * s,
          );
          this.dummy.scale.setScalar(Math.max(0.8 + ((j + k * 0.37) % 1) * 0.5, 0.001) * Math.max(s, 0.001));
          this.dummy.rotation.set(0, 0, 0);
          this.dummy.updateMatrix();
          this.berries.setMatrixAt(b * 3 + k, this.dummy.matrix);
        }
        b++;
      }
    }
    this.hideRest(this.trunks, t);
    this.hideRest(this.canopies, tpn);
    this.hideRest(this.crowns, tc);
    this.hideRest(this.poplars, tp);
    this.hideRest(this.gold, gd * 3);
    this.hideRest(this.nuggets, gd * 2);
    this.hideRest(this.dirt, gd);
    this.hideRest(this.bushes, b);
    this.hideRest(this.berries, b * 3);
    for (const m of [this.trunks, this.canopies, this.crowns, this.poplars, this.gold, this.nuggets, this.dirt, this.bushes, this.berries]) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
  }

  private placeSpin(m: THREE.InstancedMesh, i: number, x: number, y: number, z: number, s: number, spin: number): void {
    this.dummy.position.set(x, y, z);
    this.dummy.scale.setScalar(Math.max(s, 0.001));
    this.dummy.rotation.set(0, spin, 0);
    this.dummy.updateMatrix();
    m.setMatrixAt(i, this.dummy.matrix);
  }

  private hideRest(m: THREE.InstancedMesh, from: number): void {
    this.dummy.position.set(0, -50, 0);
    this.dummy.scale.setScalar(0.001);
    this.dummy.updateMatrix();
    for (let i = from; i < (m as unknown as { count: number }).count; i++) {
      m.setMatrixAt(i, this.dummy.matrix);
    }
  }

  clear(): void {
    this.hideRest(this.trunks, 0);
    this.hideRest(this.canopies, 0);
    this.hideRest(this.crowns, 0);
    this.hideRest(this.poplars, 0);
    this.hideRest(this.gold, 0);
    this.hideRest(this.nuggets, 0);
    this.hideRest(this.dirt, 0);
    this.hideRest(this.bushes, 0);
    this.hideRest(this.berries, 0);
  }
}

// LakeRenderer draws the match's blocking lakes as irregular shoreline
// blobs (same lobe math as the server): a translucent blue disc with a
// sandy shore skirt. Lakes are static per match, so geometries rebuild only
// when the shape signature changes — never per frame.
const LAKE_SLOTS = 4;
const LAKE_SEGS = 40;

function lakeEdgeFactor(l: Lake, theta: number): number {
  return 1 + l.a1 * Math.sin(2 * theta + l.p1) + l.a2 * Math.sin(3 * theta + l.p2);
}

function lakeSig(l: Lake): string {
  return [l.x, l.z, l.rx, l.rz, l.a1, l.p1, l.a2, l.p2].map((v) => v.toFixed(3)).join(',');
}

// Blob ShapeGeometry in world XZ (shape Y maps to -Z after the -90° pitch,
// so sample with negated Z to land upright).
function blobGeometry(l: Lake, grow: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  for (let i = 0; i <= LAKE_SEGS; i++) {
    const t = (i / LAKE_SEGS) * Math.PI * 2;
    const e = lakeEdgeFactor(l, t);
    const px = l.rx * grow * e * Math.cos(t);
    const pz = l.rz * grow * e * Math.sin(t);
    if (i === 0) shape.moveTo(px, -pz);
    else shape.lineTo(px, -pz);
  }
  return new THREE.ShapeGeometry(shape);
}

export class LakeRenderer {
  private slots: Array<{ water: THREE.Mesh; shore: THREE.Mesh; sig: string }> = [];

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < LAKE_SLOTS; i++) {
      const water = new THREE.Mesh(
        new THREE.BufferGeometry(),
        new THREE.MeshLambertMaterial({ color: 0x2f6fb4, transparent: true, opacity: 0.85 }),
      );
      const shore = new THREE.Mesh(
        new THREE.BufferGeometry(),
        new THREE.MeshLambertMaterial({ color: 0xcbb37a, transparent: true, opacity: 0.9 }),
      );
      for (const m of [water, shore]) {
        m.rotation.x = -Math.PI / 2;
        m.frustumCulled = false;
        m.renderOrder = 1; // above ground, below bars/overlays
        m.visible = false;
        scene.add(m);
      }
      this.slots.push({ water, shore, sig: '' });
    }
  }

  sync(lakes: Lake[]): void {
    for (let i = 0; i < LAKE_SLOTS; i++) {
      const s = this.slots[i];
      const l = lakes[i];
      if (!l) {
        s.water.visible = s.shore.visible = false;
        s.sig = '';
        continue;
      }
      const sig = lakeSig(l);
      if (sig !== s.sig) {
        s.sig = sig;
        s.water.geometry.dispose();
        s.shore.geometry.dispose();
        s.water.geometry = blobGeometry(l, 1);
        s.shore.geometry = blobGeometry(l, 1.07);
        s.water.position.set(l.x, 0.0, l.z);
        s.shore.position.set(l.x, -0.01, l.z);
      }
      s.water.visible = s.shore.visible = true;
    }
  }

  clear(): void {
    for (const s of this.slots) {
      s.water.visible = s.shore.visible = false;
      s.sig = '';
    }
  }
}

// Resource colors shared by hover rings + order pings.
export function resColor(kind: string): string {
  return kind === 'wood' ? '#22c55e' : kind === 'gold' ? '#eab308' : '#f472b6';
}

// MarkerPool: transient order-confirmation pings (expanding rings) plus one
// hover ring that previews what a right-click would assign.
export class MarkerPool {
  private pings: THREE.InstancedMesh;
  private hoverRing: THREE.Mesh;
  private live: Array<{ x: number; z: number; life: number; max: number; color: string }> = [];
  private dummy = new THREE.Object3D();
  private tmpColor = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.pings = new THREE.InstancedMesh(
      new THREE.RingGeometry(0.9, 1.15, 24),
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0.95,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
      24,
    );
    this.pings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.pings.frustumCulled = false;
    for (let i = 0; i < 24; i++) this.pings.setColorAt(i, this.tmpColor.set('#ffffff'));
    scene.add(this.pings);

    this.hoverRing = new THREE.Mesh(
      new THREE.RingGeometry(1.3, 1.7, 24),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.hoverRing.rotation.x = -Math.PI / 2;
    this.hoverRing.position.y = 0.1;
    this.hoverRing.visible = false;
    scene.add(this.hoverRing);
  }

  ping(x: number, z: number, color: string): void {
    if (this.live.length >= 24) this.live.shift();
    this.live.push({ x, z, life: 0.9, max: 0.9, color });
  }

  setHover(x: number, z: number, color: string): void {
    this.hoverRing.visible = true;
    this.hoverRing.position.set(x, 0.1, z);
    (this.hoverRing.material as THREE.MeshBasicMaterial).color.set(color);
  }

  clearHover(): void {
    this.hoverRing.visible = false;
  }

  // Full reset between matches: drop in-flight pings too (frozen while in
  // the lobby, they'd otherwise replay over the new map).
  clear(): void {
    this.live = [];
    this.clearHover();
    this.dummy.position.set(0, -50, 0);
    this.dummy.scale.setScalar(0.001);
    this.dummy.updateMatrix();
    for (let i = 0; i < 24; i++) this.pings.setMatrixAt(i, this.dummy.matrix);
    this.pings.instanceMatrix.needsUpdate = true;
  }

  update(dt: number): void {
    let n = 0;
    for (const p of this.live) {
      p.life -= dt;
      if (p.life <= 0 || n >= 24) continue;
      const t = 1 - p.life / p.max;
      const s = 1 + t * 2.2;
      this.dummy.position.set(p.x, 0.12, p.z);
      this.dummy.rotation.set(-Math.PI / 2, 0, 0);
      this.dummy.scale.set(s, s, 1);
      this.dummy.updateMatrix();
      this.pings.setMatrixAt(n, this.dummy.matrix);
      this.pings.setColorAt(n, this.tmpColor.set(p.color));
      n++;
    }
    this.live = this.live.filter((p) => p.life > 0);
    this.dummy.position.set(0, -50, 0);
    this.dummy.scale.setScalar(0.001);
    this.dummy.updateMatrix();
    for (let i = n; i < 24; i++) this.pings.setMatrixAt(i, this.dummy.matrix);
    this.pings.instanceMatrix.needsUpdate = true;
    if (this.pings.instanceColor) this.pings.instanceColor.needsUpdate = true;
  }
}

// TracerPool draws ranged attacks: archer arrows plus the watchtower
// ladder — heavy bolts → fast bullets → missiles → lobbed bombs — plus an
// impact flash where each one lands. Three instanced meshes cover hundreds
// of in-flight projectiles; tiers read via size, color and trajectory.
const BOLT_COLORS = ['#ffc266', '#fef9c3', '#fb923c', '#ef4444'];
const BOLT_SCALE = [1, 0.75, 1.25, 1.8];
const IMPACT_COLORS = ['#ffe08a', '#fef9c3', '#fdba74', '#fca5a5'];

export class TracerPool {
  private arrows: THREE.InstancedMesh;
  private bolts: THREE.InstancedMesh;
  private impacts: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private tmpColor = new THREE.Color();
  private live: Array<{ x1: number; z1: number; x2: number; z2: number; life: number; max: number; dist: number; tower: boolean; lvl: number }> = [];
  private hits: Array<{ x: number; z: number; y: number; life: number; max: number; big: number }> = [];

  constructor(scene: THREE.Scene) {
    // Arrow: wooden shaft along +Z with a head cone at the tip.
    const shaft = new THREE.CylinderGeometry(0.035, 0.035, 0.85, 5);
    shaft.rotateX(Math.PI / 2);
    const head = new THREE.ConeGeometry(0.08, 0.22, 6);
    head.rotateX(Math.PI / 2); // tip (+Y) -> +Z
    head.translate(0, 0, 0.5);
    const arrowGeo = mergeGeometries([shaft, head]) ?? shaft;
    this.arrows = new THREE.InstancedMesh(arrowGeo, new THREE.MeshLambertMaterial({ color: 0xd8c49a }), 64);
    this.bolts = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.3, 0.3, 1.1),
      new THREE.MeshBasicMaterial({ color: 0xffffff }),
      32,
    );
    this.impacts = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.32, 8, 6),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 }),
      32,
    );
    for (let i = 0; i < 32; i++) {
      this.bolts.setColorAt(i, this.tmpColor.set('#ffc266'));
      this.impacts.setColorAt(i, this.tmpColor.set('#ffe08a'));
    }
    for (const m of [this.arrows, this.bolts, this.impacts]) {
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.frustumCulled = false;
      scene.add(m);
    }
    this.dummy.rotation.order = 'YXZ';
  }

  spawn(shots: Shot[]): void {
    for (const s of shots) {
      if (this.live.length > 90) this.live.shift();
      const dist = Math.hypot(s.x2 - s.x1, s.z2 - s.z1);
      // Tower tiers: bullets fly flat and fast, bombs lob slowly.
      const lvl = s.k === 'bullet' ? 1 : s.k === 'missile' ? 2 : s.k === 'bomb' ? 3 : 0;
      const tower = s.k === 'tower' || lvl > 0;
      const speed = s.k === 'bullet' ? 34 : s.k === 'bomb' ? 20 : 26;
      // Flight time grows with range so long shots visibly travel.
      const max = Math.min(0.5, Math.max(0.12, dist / speed));
      this.live.push({ x1: s.x1, z1: s.z1, x2: s.x2, z2: s.z2, life: max, max, dist, tower, lvl });
      if (tower) {
        // Muzzle flash at the tower top so the launch reads clearly —
        // bigger tiers flash bigger.
        if (this.hits.length > 30) this.hits.shift();
        this.hits.push({ x: s.x1, z: s.z1, life: 0.18, max: 0.18, y: 6.8, big: lvl });
      }
    }
  }

  update(dt: number): void {
    let na = 0;
    let nb = 0;
    for (const p of this.live) {
      p.life -= dt;
      const done = p.life <= 0;
      const t = done ? 1 : 1 - p.life / p.max;
      // Bombs lob high; bullets stay almost flat.
      const arcMul = p.tower ? (p.lvl === 3 ? 1.7 : p.lvl === 1 ? 0.5 : 1) : 1;
      const arcH = Math.min(2.6 * arcMul, p.dist * 0.18 * arcMul);
      const x = p.x1 + (p.x2 - p.x1) * t;
      const z = p.z1 + (p.z2 - p.z1) * t;
      const y = 1.1 + Math.sin(t * Math.PI) * arcH;
      // Nose along the flight path: yaw from travel dir, pitch from arc slope.
      const slope = (Math.PI * arcH * Math.cos(t * Math.PI)) / p.max;
      const hspeed = Math.max(p.dist / p.max, 0.001);
      this.dummy.position.set(x, y, z);
      this.dummy.rotation.set(-Math.atan2(slope, hspeed), Math.atan2(p.x2 - p.x1, p.z2 - p.z1), 0);
      if (p.tower) {
        if (nb < 32) {
          const sc = BOLT_SCALE[Math.min(p.lvl, 3)];
          this.dummy.scale.set(sc, sc, sc * (p.lvl === 1 ? 0.7 : 1));
          this.dummy.updateMatrix();
          this.bolts.setMatrixAt(nb, this.dummy.matrix);
          this.bolts.setColorAt(nb, this.tmpColor.set(BOLT_COLORS[Math.min(p.lvl, 3)]));
          nb++;
        }
      } else {
        if (na < 64) {
          this.dummy.scale.set(1, 1, 1);
          this.dummy.updateMatrix();
          this.arrows.setMatrixAt(na++, this.dummy.matrix);
        }
      }
      if (done) {
        if (this.hits.length > 30) this.hits.shift();
        this.hits.push({ x: p.x2, z: p.z2, y: 1.0, life: 0.22, max: 0.22, big: p.tower ? p.lvl : 0 });
      }
    }
    this.live = this.live.filter((p) => p.life > 0);
    this.hideRest(this.arrows, na, 64);
    this.hideRest(this.bolts, nb, 32);
    this.arrows.instanceMatrix.needsUpdate = true;
    this.bolts.instanceMatrix.needsUpdate = true;
    if (this.bolts.instanceColor) this.bolts.instanceColor.needsUpdate = true;

    // Impact flashes: pop up fast, then shrink away. Higher tiers burst bigger.
    let ni = 0;
    for (const h of this.hits) {
      h.life -= dt;
      if (h.life <= 0 || ni >= 32) continue;
      const s = (0.25 + Math.sin(((1 - h.life / h.max) * Math.PI)) * 1.1) * (1 + h.big * 0.55);
      this.dummy.position.set(h.x, h.y, h.z);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(Math.max(s, 0.001));
      this.dummy.updateMatrix();
      this.impacts.setMatrixAt(ni, this.dummy.matrix);
      this.impacts.setColorAt(ni, this.tmpColor.set(IMPACT_COLORS[Math.min(h.big, 3)]));
      ni++;
    }
    this.hits = this.hits.filter((h) => h.life > 0);
    this.hideRest(this.impacts, ni, 32);
    this.impacts.instanceMatrix.needsUpdate = true;
    if (this.impacts.instanceColor) this.impacts.instanceColor.needsUpdate = true;
  }

  private hideRest(m: THREE.InstancedMesh, from: number, cap: number): void {
    this.dummy.position.set(0, -50, 0);
    this.dummy.rotation.set(0, 0, 0);
    this.dummy.scale.setScalar(0.001);
    this.dummy.updateMatrix();
    for (let i = from; i < cap; i++) m.setMatrixAt(i, this.dummy.matrix);
    this.dummy.scale.set(1, 1, 1);
  }

  // Full reset between matches: drop in-flight projectiles and impacts too
  // (frozen while in the lobby, they'd otherwise replay over the new map).
  clear(): void {
    this.live = [];
    this.hits = [];
    this.hideRest(this.arrows, 0, 64);
    this.hideRest(this.bolts, 0, 32);
    this.hideRest(this.impacts, 0, 32);
    this.arrows.instanceMatrix.needsUpdate = true;
    this.bolts.instanceMatrix.needsUpdate = true;
    this.impacts.instanceMatrix.needsUpdate = true;
  }
}
